'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useState, type ReactNode } from 'react';
import { BoaFormContainer, type BoaPartner } from './BoaFormContainer';

interface BoaLeadFlowProps {
  partner?: BoaPartner;
  thankYouPrefix: string;
  hero: ReactNode;
  disclaimers: ReactNode;
  aside: ReactNode;
  featuredIn: ReactNode;
  benefits: ReactNode;
}

export const BoaLeadFlow = ({
  partner,
  thankYouPrefix,
  hero,
  disclaimers,
  aside,
  featuredIn,
  benefits,
}: BoaLeadFlowProps) => {
  const [submittedName, setSubmittedName] = useState<string | null>(null);

  if (submittedName !== null) {
    return <ThankYou prefix={thankYouPrefix} name={submittedName} />;
  }

  return (
    <>
      {hero}

      <section className='bg-sh-yellow px-7 py-16 lg:px-12 lg:py-24'>
        <div className='mx-auto flex max-w-[1280px] flex-col gap-12 lg:flex-row lg:gap-[100px]'>
          <div className='lg:flex-1'>
            <div id='boa-form'>
              <BoaFormContainer
                partner={partner}
                onSuccess={setSubmittedName}
              />
            </div>
            {disclaimers}
          </div>
          {aside}
        </div>
      </section>

      {featuredIn}
      {benefits}
    </>
  );
};

const ThankYou = ({ prefix, name }: { prefix: string; name: string }) => {
  return (
    <section className='bg-sh-yellow flex min-h-svh flex-col-reverse justify-center xl:flex-row xl:items-center'>
      <div className='flex flex-col justify-center px-7 py-16 xl:w-1/2 xl:px-29 xl:py-0'>
        <h3 className='text-sh-navy font-sans text-[23px] leading-[130%] font-light'>
          {prefix}, {name}.
        </h3>
        <Link
          href='/'
          className='bg-sh-sky text-sh-navy mt-8 inline-flex h-12.5 w-fit items-center justify-center rounded-full px-6 font-sans text-[14px] transition-opacity hover:opacity-90'
        >
          Return to homepage
        </Link>
      </div>
      <div className='relative aspect-1186/896 w-full xl:w-1/2'>
        <Image
          src='/bank-of-america/thank-you.jpg'
          alt=''
          fill
          sizes='(min-width: 1280px) 50vw, 100vw'
          className='object-cover'
        />
      </div>
    </section>
  );
};
